import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, PawPrint, Lock, Phone, Mail, MapPin, Globe, Facebook, Instagram, Linkedin } from "lucide-react";
import api from "../lib/api";
import { useSiteContent } from "../hooks/useSiteContent";

const XFooterIcon = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const links = [
  { to: "/", label: "Home" },
  { to: "/meet-our-dogs", label: "Meet Our Dogs" },
  { to: "/blog", label: "Blog" },
  { to: "/about-us", label: "About Us" },
  { to: "/contact-us", label: "Contact Us" },
];

const footerLinks = [...links, { to: "/dogs-for-sale", label: "Dogs For Sale" }];

export const Logo = ({ compact = false }) => {
  const site = useSiteContent();
  return (
    <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
      <span className="relative inline-flex items-center justify-center">
        <span className="absolute h-12 w-12 rounded-full bg-brand-crimson/40 blur-lg transition-all duration-300 group-hover:bg-brand-crimson/60" />
        <img
          src="/logo.png"
          alt="Quest K9's Detection Dogs"
          className="relative h-12 w-12 sm:h-16 sm:w-16 object-contain drop-shadow-[0_4px_16px_rgba(0,0,0,0.5)] transition-transform duration-300 group-hover:scale-110"
        />
      </span>
      {!compact && (
        <span className="leading-none">
          <span className="font-display font-bold text-lg text-white tracking-wide block">{site?.pages?.logo_title || "QUEST K9's"}</span>
          <span className="block font-eyebrow text-[9px] tracking-[0.4em] text-brand-crimson font-bold mt-1">{site?.pages?.logo_subtitle || "DETECTION DOGS"}</span>
        </span>
      )}
    </Link>
  );
};

export function SiteNav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const site = useSiteContent();
  const navBtn = site?.chrome?.nav_request_button || "Request a Sweep";
  const navLabels = site?.chrome?.nav_labels || {};
  const navItems = links.map((l) => ({ ...l, label: navLabels[l.to] || l.label }));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-brand-obsidian/90 backdrop-blur-md border-b border-brand-line" : "bg-transparent"
      }`}
      data-testid="site-nav"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 h-16 sm:h-20 flex items-center justify-between">
        <Logo />
        <nav className="hidden md:flex items-center gap-8">
          {navItems.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
              className={({ isActive }) =>
                `font-eyebrow text-[11px] uppercase tracking-[0.2em] font-semibold transition-colors ${
                  isActive ? "text-brand-crimson" : "text-white/70 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/contact-us"
            data-testid="nav-cta-button"
            className="bg-brand-crimson hover:bg-brand-crimhover text-white font-eyebrow text-[11px] uppercase tracking-[0.2em] font-bold px-5 py-2.5 rounded-md transition-all duration-300 hover:shadow-[0_0_24px_rgba(200,16,46,0.5)]"
          >
            {navBtn}
          </Link>
        </nav>
        <button className="md:hidden text-white p-2" data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)} aria-label="Toggle menu">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-brand-obsidian/95 backdrop-blur-md border-b border-brand-line overflow-hidden"
          >
            <div className="px-4 py-4 flex flex-col gap-1">
              {navItems.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  className="py-3 text-white/80 hover:text-white border-b border-brand-line/60 font-eyebrow uppercase tracking-[0.2em] text-xs"
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/contact-us"
                onClick={() => setOpen(false)}
                className="mt-3 mb-2 bg-brand-crimson text-white text-center py-3 rounded-md font-eyebrow uppercase tracking-[0.2em] text-xs font-bold"
              >
                {navBtn}
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

export function Marquee({ items: propItems }) {
  const items =
    propItems && propItems.length
      ? propItems
      : ["NASDU Accredited", "BS 8517 Compliant", "SIA Licensed Handlers", "Explosives Detection", "Narcotics Detection", "Knife & Weapon Detection", "24/7 Deployment"];
  return (
    <div className="relative z-20 -mt-6 bg-brand-crimson -rotate-1 scale-[1.03] py-3.5 overflow-hidden shadow-[0_10px_40px_rgba(200,16,46,0.25)]" data-testid="marquee-band">
      <div className="flex w-max animate-marquee">
        {[0, 1].map((half) => (
          <div key={half} className="flex items-center shrink-0">
            {items.map((t, i) => (
              <span
                key={`${half}-${i}`}
                className="flex items-center gap-8 pr-8 text-[13px] font-eyebrow font-semibold tracking-[0.25em] uppercase text-white whitespace-nowrap"
              >
                {t}
                <PawPrint className="h-4 w-4 text-white/70" />
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export function SiteFooter() {
  const content = useSiteContent();
  const c = content?.contact || {};
  const [socialLinks, setSocialLinks] = useState({ facebook_url: "", x_url: "", linkedin_url: "", instagram_url: "" });

  useEffect(() => {
    api
      .get("/social/links")
      .then((r) => setSocialLinks(r.data || { facebook_url: "", x_url: "", linkedin_url: "", instagram_url: "" }))
      .catch(() => setSocialLinks({ facebook_url: "", x_url: "", linkedin_url: "", instagram_url: "" }));
  }, []);

  return (
    <footer className="bg-brand-obsidian border-t border-brand-line" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-14 grid gap-10 md:grid-cols-3">
        <div>
          <Logo />
          <p className="mt-5 text-sm text-white/50 max-w-xs leading-relaxed" data-testid="footer-description">
            {content?.pages?.footer_text || "Accredited sniffer dog and knife detection teams. Protecting venues, schools and events across the UK."}
          </p>
          {(socialLinks.facebook_url || socialLinks.x_url) && (
            <div className="mt-5 flex items-center gap-3" data-testid="footer-social-links">
              {socialLinks.facebook_url && (
                <a
                  href={socialLinks.facebook_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow us on Facebook"
                  data-testid="footer-social-facebook"
                  className="flex h-9 w-9 items-center justify-center rounded-md border border-brand-line bg-brand-surface text-white/60 transition-all duration-300 hover:text-[#1877F2] hover:border-[#1877F2]/60 hover:shadow-[0_0_18px_rgba(24,119,242,0.35)]"
                >
                  <Facebook className="h-4 w-4" />
                </a>
              )}
              {socialLinks.x_url && (
                <a
                  href={socialLinks.x_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow us on X"
                  data-testid="footer-social-x"
                  className="flex h-9 w-9 items-center justify-center rounded-md border border-brand-line bg-brand-surface text-white/60 transition-all duration-300 hover:text-white hover:border-white/60 hover:shadow-[0_0_18px_rgba(255,255,255,0.2)]"
                >
                  <XFooterIcon className="h-3.5 w-3.5" />
                </a>
              )}
              {socialLinks.instagram_url && (
                <a
                  href={socialLinks.instagram_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow us on Instagram"
                  data-testid="footer-social-instagram"
                  className="flex h-9 w-9 items-center justify-center rounded-md border border-brand-line bg-brand-surface text-white/60 transition-all duration-300 hover:text-[#E1306C] hover:border-[#E1306C]/60 hover:shadow-[0_0_18px_rgba(225,48,108,0.35)]"
                >
                  <Instagram className="h-4 w-4" />
                </a>
              )}
              {socialLinks.linkedin_url && (
                <a
                  href={socialLinks.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow us on LinkedIn"
                  data-testid="footer-social-linkedin"
                  className="flex h-9 w-9 items-center justify-center rounded-md border border-brand-line bg-brand-surface text-white/60 transition-all duration-300 hover:text-[#0A66C2] hover:border-[#0A66C2]/60 hover:shadow-[0_0_18px_rgba(10,102,194,0.35)]"
                >
                  <Linkedin className="h-4 w-4" />
                </a>
              )}
            </div>
          )}
        </div>
        <div>
          <p className="font-eyebrow text-[11px] uppercase tracking-[0.3em] text-brand-crimson font-bold mb-4">Navigate</p>
          <ul className="space-y-2.5">
            {footerLinks.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="text-sm text-white/60 hover:text-white transition-colors" data-testid={`footer-link-${l.to === "/" ? "home" : l.to.slice(1)}`}>
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="font-eyebrow text-[11px] uppercase tracking-[0.3em] text-brand-crimson font-bold mb-4">Contact</p>
          <ul className="space-y-2.5 text-sm text-white/60">
            {c.phone && (
              <li className="flex items-center gap-2.5">
                <Phone className="h-4 w-4 text-brand-crimson shrink-0" />
                <a href={`tel:${c.phone}`} className="hover:text-white transition-colors">{c.phone}</a>
              </li>
            )}
            {c.email && (
              <li className="flex items-center gap-2.5">
                <Mail className="h-4 w-4 text-brand-crimson shrink-0" />
                <a href={`mailto:${c.email}`} className="hover:text-white transition-colors">{c.email}</a>
              </li>
            )}
            {c.coverage && (
              <li className="flex items-center gap-2.5">
                <MapPin className="h-4 w-4 text-brand-crimson shrink-0" />
                {c.coverage}
              </li>
            )}
            {c.website && (
              <li className="flex items-center gap-2.5">
                <Globe className="h-4 w-4 text-brand-crimson shrink-0" />
                <a href={`https://${c.website}`} className="hover:text-white transition-colors" data-testid="footer-website">{c.website}</a>
              </li>
            )}
          </ul>
        </div>
      </div>
      <div className="border-t border-brand-line">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-white/40">© {new Date().getFullYear()} Quest K9's Detection Dogs. All rights reserved.</p>
          <Link
            to="/admin/login"
            data-testid="footer-owner-login"
            className="text-xs text-white/40 hover:text-brand-crimson inline-flex items-center gap-1.5 transition-colors"
          >
            <Lock className="h-3 w-3" /> Owner Login
          </Link>
        </div>
      </div>
    </footer>
  );
}
